import { SagaIterator } from 'redux-saga';
import { put, takeEvery } from 'redux-saga/effects';

import {
  OpenConnectedApplicationDialog,
  OpenEASConfigurationDialog,
  ToggleConnectedApplicationEnablement
} from '~src/bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '~src/codegen/Localize';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { Location } from '~src/commonInfra/utils/Location';
import { ConnectedApplicationType, IConnectedApplicationInfo } from '~src/connectedApplications/models/ConnectedApplicationInfo';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { IProjectInfo } from '~src/projects/models/IProjectInfo';
import { Projects } from '~src/projects/resources/Projects';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';

import {
  OpenConnectedApplicationDialogAction,
  ToggleConnectedApplicationEnablementAction
} from '../actions/ConnectedApplicationActions';
import { openConnectedApplicationDirectTrustDialog } from '../components/ConnectedApplicationDialog';
import { openConnectedApplicationEasDialog } from '../components/ConnectedApplicationEasDialog';
import { ConnectedApplications } from '../resources/ConnectedApplicationsResource';
import { CreateConnectedApplicationResult } from '../serverApi/requests/CreateConnectedApplication';
import { CreateEasConnectedApplicationResult } from '../serverApi/requests/CreateConnectedApplicationEas';
import { callGetConnectedApplication, IGetConnectedApplicationResult } from '../serverApi/requests/GetConnectedApplication';
import { callGetConnectedApplicationEas, IGetConnectedApplicationEasResult } from '../serverApi/requests/GetConnectedApplicationEas';

export function* handleOpenConnectedApplicationDialogAction(action: OpenConnectedApplicationDialogAction) {
  try {
    // The Project endpoint automatically handles filtering to the correct site (using m_projectService.getProjectsForUserSite)
    // see: https://sourcegraph.prod.tableautools.com/teams/near/-/blob/workgroup/src/silos/tableau-server/services/war-vizportal/src/com/tableausoftware/api/webclient/impl/WebClientProjectAppService.java#L562:63
    const projects: ResourceQuery<IProjectInfo> = Projects.all();
    const numProjects: number = yield projects.getCount();
    const projectsResponse: ListResult<IProjectInfo> = yield projects.getSlice(0, numProjects);
    const isOnDetailsPage = action.previous && Location.instance.href().includes(action.previous.id);
    const isOnGridLayout = action.previous && !Location.instance.href().includes(action.previous.id);
    if (isOnGridLayout) { // clicking edit on grid layout will produce an action.previous that doesn't have complete connected app details (need to perform additional call)
      const connectedApp: IGetConnectedApplicationResult = yield callGetConnectedApplication(action.previous.id);

      const completeActionPrevious: IConnectedApplicationInfo = {
        enabled: connectedApp.connectedApplication.enabled,
        authorizationServerType: connectedApp.connectedApplication.authorizationServerType,
        unrestrictedEmbedding: connectedApp.connectedApplication.unrestrictedEmbedding,
        domainSafelist: connectedApp.connectedApplication.domainSafelist,
        projectId: connectedApp.connectedApplication.projectId,
        projectIds: connectedApp.connectedApplication.projectIds.projectId,
        contentType: action.previous.contentType,
        id: action.previous.id,
        createdAt: action.previous.createdAt,
        name: action.previous.name,
      };

      action.previous = completeActionPrevious;
    }
    const creationResponse: CreateConnectedApplicationResult = yield openConnectedApplicationDirectTrustDialog(projectsResponse.items, action.previous);
    // We still request the update so if the user then clicks the back button the new connected app entry is there
    yield put(requestSpaceContents());

    if (!action.previous) {
      // When creating a new connected app we take you to the details page
      ConnectedApplications.navigateToConnectedApplicationsDetailsPage(creationResponse.connectedApplication.clientId);
    } else if (isOnDetailsPage) {
      // When editing the app in the details page, we refresh the details page.
      // Ideally we should only refresh the component not the entire vizportal page.
      // https://tfs.tsi.lan/tfs/DefaultCollection/Tableau-Dev/_workitems/edit/1322446/
      WindowLocationService.reload();
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleOpenEasDialogAction(action: OpenConnectedApplicationDialogAction) {
  try {
    const isOnDetailsPage = action.previous && Location.instance.href().includes(action.previous.id);
    const isOnGridLayout = action.previous && !Location.instance.href().includes(action.previous.id);
    if (isOnGridLayout) { // clicking edit on grid layout will produce an action.previous that doesn't have complete connected app details (need to perform additional call)
      const connectedApp: IGetConnectedApplicationEasResult = yield callGetConnectedApplicationEas(action.previous.id);

      const completeActionPrevious: IConnectedApplicationInfo = {
        enabled: connectedApp.externalAuthorizationServer.enabled,
        issuerUrl: connectedApp.externalAuthorizationServer.issuerUrl,
        jwksUri: connectedApp.externalAuthorizationServer.jwksUri,
        contentType: action.previous.contentType,
        id: action.previous.id,
        createdAt: action.previous.createdAt,
        name: action.previous.name,
      };

      action.previous = completeActionPrevious;
    }
    const creationResponse: CreateEasConnectedApplicationResult = yield openConnectedApplicationEasDialog(action.previous);
    if (!action.previous && creationResponse) {
      ConnectedApplications.navigateToEasConnectedApplicationsDetailsPage(creationResponse.externalAuthorizationServer.id);
    } else if (isOnDetailsPage) {
      WindowLocationService.reload();
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleToggleConnectedApplicationEnablementAction(action: ToggleConnectedApplicationEnablementAction): SagaIterator {
  try {
    toggleEnablement(action.application);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

async function toggleEnablement(application: IConnectedApplicationInfo) {
  let result;
  // todo: remove the undefined check once webclient call wired in
  if (application.connectedApplicationType === ConnectedApplicationType.DIRECT_TRUST) {
    result = await ConnectedApplications.updateConnectedAppsDirectTrust({
      enabled: !application.enabled,
      clientId: application.id,
    });
  } else if (application.connectedApplicationType === ConnectedApplicationType.EAS) {
    result = await ConnectedApplications.updateConnectedAppsEas({
      enabled: !application.enabled,
      id: application.id,
    });
  }
  if (result !== undefined) {
    // TODO: TFS1322446 - Ideally we should only refresh the component not the entire vizportal page.
    WindowLocationService.reload();
  }
  return result;
}

export function* rootConnectedApplicationSagas() {
  yield takeEvery(OpenConnectedApplicationDialog, handleOpenConnectedApplicationDialogAction);
  yield takeEvery(OpenEASConfigurationDialog, handleOpenEasDialogAction);
  yield takeEvery(ToggleConnectedApplicationEnablement, handleToggleConnectedApplicationEnablementAction);
}
